<template>
  <div class="client-customizer">
    <!-- Header -->
    <div class="customizer-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-magic"></i>
        </div>
        <div class="header-text">
          <h3>Quick Client Setup</h3>
          <p>Configure check requirements for your client in minutes</p>
        </div>
      </div>
      <div class="header-actions">
        <button 
          v-if="hasChanges" 
          @click="resetChanges" 
          class="btn btn-outline-secondary btn-sm"
        >
          <i class="fas fa-undo"></i> Reset
        </button>
        <button 
          @click="saveAndApply" 
          class="btn btn-primary"
          :disabled="saving || !hasChanges"
        >
          <i v-if="saving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          {{ saving ? 'Saving...' : 'Save & Apply' }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading configuration...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="customizer-content">
      <!-- Application Type Badge -->
      <div class="app-type-badge">
        <i :class="schema.icon || 'fas fa-file-alt'"></i>
        <span>{{ schema.name || applicationType }}</span>
      </div>

      <!-- Quick Presets (for COI_COMPLIANCE) -->
      <div v-if="presets.length > 0" class="presets-section">
        <label>Quick Presets:</label>
        <div class="preset-buttons">
          <button 
            v-for="preset in presets" 
            :key="preset.key"
            @click="applyPreset(preset.key)"
            :class="['preset-btn', { active: activePreset === preset.key }]"
          >
            {{ preset.name }}
          </button>
        </div>
      </div>

      <!-- Dynamic Sections based on Schema -->
      <div 
        v-for="section in schema.sections" 
        :key="section.id" 
        class="config-section"
      >
        <div class="section-header" @click="toggleSection(section.id)">
          <div class="section-title">
            <i :class="section.icon || 'fas fa-cog'"></i>
            <span>{{ section.title }}</span>
          </div>
          <i :class="expandedSections[section.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </div>

        <div v-show="expandedSections[section.id]" class="section-content">
          <!-- Subsections (for coverage limits) -->
          <template v-if="section.subsections">
            <div 
              v-for="subsection in section.subsections" 
              :key="subsection.id"
              class="subsection"
            >
              <div class="subsection-header" @click="toggleSubsection(subsection.id)">
                <span 
                  class="coverage-badge" 
                  :style="{ backgroundColor: subsection.color || '#667eea' }"
                >
                  {{ subsection.icon_badge || subsection.id.toUpperCase() }}
                </span>
                <span>{{ subsection.title }}</span>
                <i :class="expandedSubsections[subsection.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </div>
              <div v-show="expandedSubsections[subsection.id]" class="subsection-fields">
                <div 
                  v-for="field in subsection.fields" 
                  :key="field.key"
                  class="field-row"
                  :class="{ hidden: shouldHideField(field) }"
                >
                  <label>{{ field.label }}</label>
                  <component
                    :is="getFieldComponent(field.type)"
                    v-model="configValues[field.key]"
                    :field="field"
                    :disabled="saving"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- Flat Fields -->
          <template v-else>
            <div class="form-grid">
              <div 
                v-for="field in section.fields" 
                :key="field.key"
                class="field-group"
                :class="{ 
                  'full-width': field.type === 'textarea' || field.type === 'boolean',
                  hidden: shouldHideField(field)
                }"
              >
                <label v-if="field.type !== 'boolean'">
                  {{ field.label }}
                  <span v-if="field.required" class="required">*</span>
                </label>
                
                <!-- Text Input -->
                <input 
                  v-if="field.type === 'text'"
                  v-model="configValues[field.key]"
                  type="text"
                  class="form-control"
                  :placeholder="field.placeholder || ''"
                  :disabled="saving"
                />
                
                <!-- Number Input -->
                <div v-else-if="field.type === 'number'" class="number-input-wrapper">
                  <input 
                    v-model.number="configValues[field.key]"
                    type="number"
                    class="form-control"
                    :disabled="saving"
                  />
                  <span v-if="field.suffix" class="input-suffix">{{ field.suffix }}</span>
                </div>
                
                <!-- Currency Input -->
                <CurrencyInput 
                  v-else-if="field.type === 'currency'"
                  v-model="configValues[field.key]"
                  :disabled="saving"
                />
                
                <!-- Select -->
                <select 
                  v-else-if="field.type === 'select'"
                  v-model="configValues[field.key]"
                  class="form-control"
                  :disabled="saving"
                >
                  <option v-for="opt in field.options" :key="opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
                
                <!-- Boolean/Checkbox -->
                <label v-else-if="field.type === 'boolean'" class="checkbox-label">
                  <input 
                    type="checkbox"
                    v-model="configValues[field.key]"
                    :disabled="saving"
                  />
                  <span class="checkbox-text">{{ field.label }}</span>
                </label>
                
                <!-- Textarea -->
                <textarea 
                  v-else-if="field.type === 'textarea'"
                  v-model="configValues[field.key]"
                  class="form-control"
                  rows="3"
                  :placeholder="field.placeholder || ''"
                  :disabled="saving"
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Requirements Preview -->
      <div class="requirements-preview">
        <div class="preview-header" @click="showRequirements = !showRequirements">
          <div class="preview-title">
            <i class="fas fa-list-check"></i>
            <span>Requirements Preview</span>
            <span class="count-badge">{{ requirements.length }} checks</span>
          </div>
          <i :class="showRequirements ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </div>

        <div v-show="showRequirements" class="requirements-list">
          <div 
            v-for="req in requirements" 
            :key="req.id" 
            class="requirement-item"
            :class="{ 'updated': updatedRequirementIds.includes(req.id) }"
          >
            <div class="req-severity" :class="req.severity?.toLowerCase()">
              {{ req.severity?.[0] || '?' }}
            </div>
            <div class="req-content">
              <div class="req-name">{{ req.name }}</div>
              <div class="req-value">{{ getRequirementDisplayValue(req) }}</div>
            </div>
            <div class="req-actions">
              <button 
                @click="openInLab(req)" 
                class="btn-icon" 
                title="Edit in Lab"
              >
                <i class="fas fa-flask"></i>
              </button>
              <button 
                @click="testRequirement(req)" 
                class="btn-icon" 
                title="Quick Test"
                :disabled="testing || !hasDocuments"
              >
                <i :class="testing && testingReqId === req.id ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Test Result Panel -->
      <div v-if="testResult" class="test-result-panel">
        <div class="test-header">
          <span><i class="fas fa-vial"></i> Test Result: {{ lastTestedRequirement?.name }}</span>
          <button @click="testResult = null" class="btn-close-sm">×</button>
        </div>
        <div class="test-content">
          <div :class="['result-badge', testResult.result?.toLowerCase()]">
            {{ testResult.result }}
          </div>
          <div class="result-evidence">{{ testResult.evidence }}</div>
          <div v-if="testResult.reasoning" class="result-reasoning">
            <strong>Reasoning:</strong> {{ testResult.reasoning }}
          </div>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="success-message">
        <i class="fas fa-check-circle"></i>
        {{ successMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, reactive } from 'vue';
import api from '@/services/api';
import CurrencyInput from '@/components/CurrencyInput.vue';

export default {
  name: 'ClientRequirementsCustomizer',
  components: {
    CurrencyInput
  },
  props: {
    checkDefinitionId: {
      type: [Number, String],
      required: true
    },
    projectId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['saved', 'open-lab'],
  
  setup(props, { emit }) {
    // State
    const loading = ref(true);
    const saving = ref(false);
    const testing = ref(false);
    const testingReqId = ref(null);
    const activePreset = ref(null);
    const successMessage = ref(null);
    const testResult = ref(null);
    const lastTestedRequirement = ref(null);
    const updatedRequirementIds = ref([]);
    const showRequirements = ref(true);
    
    // Schema and data
    const applicationType = ref('');
    const schema = ref({ sections: [] });
    const presets = ref([]);
    const requirements = ref([]);
    const documents = ref([]);
    const configValues = reactive({});
    const originalConfig = ref({});
    
    // Expanded states
    const expandedSections = reactive({});
    const expandedSubsections = reactive({});
    
    // Computed
    const hasChanges = computed(() => {
      return JSON.stringify(configValues) !== JSON.stringify(originalConfig.value);
    });
    
    const hasDocuments = computed(() => documents.value.length > 0);
    
    // Methods
    const loadConfigSchema = async () => {
      loading.value = true;
      try {
        const response = await api.get(`/comprehensive-check-definitions/${props.checkDefinitionId}/config_schema/`);
        
        if (response.data.success) {
          applicationType.value = response.data.application_type;
          schema.value = response.data.schema;
          presets.value = response.data.presets || [];
          requirements.value = response.data.requirements || [];
          
          // Initialize config values with defaults and current values
          initializeConfigValues(response.data.current_values || {});
          
          // Expand first section by default
          if (schema.value.sections?.length > 0) {
            expandedSections[schema.value.sections[0].id] = true;
          }
          
          // Expand first subsection of each section
          schema.value.sections?.forEach(section => {
            if (section.subsections?.length > 0) {
              expandedSubsections[section.subsections[0].id] = true;
            }
          });
        }
      } catch (error) {
        console.error('Error loading config schema:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const loadDocuments = async () => {
      try {
        const response = await api.get(`/projects/${props.projectId}/documents/`);
        documents.value = response.data.documents || [];
      } catch (error) {
        console.error('Error loading documents:', error);
      }
    };
    
    const initializeConfigValues = (currentValues) => {
      // Clear existing values
      Object.keys(configValues).forEach(key => delete configValues[key]);
      
      // Set defaults from schema
      schema.value.sections?.forEach(section => {
        if (section.subsections) {
          section.subsections.forEach(subsection => {
            subsection.fields?.forEach(field => {
              configValues[field.key] = currentValues[field.key] ?? field.default ?? getDefaultForType(field.type);
            });
          });
        } else {
          section.fields?.forEach(field => {
            configValues[field.key] = currentValues[field.key] ?? field.default ?? getDefaultForType(field.type);
          });
        }
      });
      
      // Handle nested values (like limits)
      if (currentValues.limits) {
        Object.assign(configValues, currentValues.limits);
      }
      if (currentValues.certificate_holder) {
        configValues.certificate_holder_name = currentValues.certificate_holder.name;
        configValues.certificate_holder_address = currentValues.certificate_holder.address;
      }
      if (currentValues.additional_insured) {
        configValues.additional_insured_name = currentValues.additional_insured.name;
        configValues.additional_insured_operations = currentValues.additional_insured.operations;
      }
      
      // Store original for comparison
      originalConfig.value = JSON.parse(JSON.stringify(configValues));
    };
    
    const getDefaultForType = (type) => {
      switch (type) {
        case 'boolean': return false;
        case 'number':
        case 'currency': return 0;
        default: return '';
      }
    };
    
    const getFieldComponent = (type) => {
      if (type === 'currency') return 'CurrencyInput';
      return 'div'; // Fallback
    };
    
    const shouldHideField = (field) => {
      if (!field.depends_on) return false;
      
      for (const [key, expectedValue] of Object.entries(field.depends_on)) {
        if (configValues[key] !== expectedValue) {
          return true;
        }
      }
      return false;
    };
    
    const toggleSection = (sectionId) => {
      expandedSections[sectionId] = !expandedSections[sectionId];
    };
    
    const toggleSubsection = (subsectionId) => {
      expandedSubsections[subsectionId] = !expandedSubsections[subsectionId];
    };
    
    const applyPreset = (presetKey) => {
      activePreset.value = presetKey;
      
      // Find the preset
      const preset = presets.value.find(p => p.key === presetKey);
      if (preset?.limits) {
        Object.entries(preset.limits).forEach(([key, value]) => {
          if (key in configValues) {
            configValues[key] = value;
          }
        });
      }
    };
    
    const saveAndApply = async () => {
      saving.value = true;
      successMessage.value = null;
      
      try {
        // Build the payload based on application type
        const payload = buildPayload();
        
        if (activePreset.value) {
          payload.apply_preset = activePreset.value;
        }
        
        const response = await api.post(
          `/comprehensive-check-definitions/${props.checkDefinitionId}/bulk_customize/`,
          payload
        );
        
        if (response.data.success) {
          // Track updated requirements for highlighting
          updatedRequirementIds.value = response.data.updated_requirements?.map(r => r.id) || [];
          
          // Update original config
          originalConfig.value = JSON.parse(JSON.stringify(configValues));
          
          // Show success message
          successMessage.value = response.data.message;
          
          // Emit event
          emit('saved', response.data);
          
          // Clear highlight after 3 seconds
          setTimeout(() => {
            updatedRequirementIds.value = [];
            successMessage.value = null;
          }, 3000);
          
          // Reload requirements to show updated values
          await loadConfigSchema();
        }
      } catch (error) {
        console.error('Error saving configuration:', error);
        alert('Failed to save: ' + (error.response?.data?.error || error.message));
      } finally {
        saving.value = false;
      }
    };
    
    const buildPayload = () => {
      // Build limits object from config values
      const limits = {};
      const limitKeys = [
        'gl_each_occurrence', 'gl_general_aggregate', 'gl_products_completed_ops',
        'gl_personal_advertising', 'gl_damage_rented_premises', 'gl_medical_expense',
        'wc_each_accident', 'wc_disease_policy', 'wc_disease_each',
        'auto_combined_single', 'umb_each_occurrence', 'umb_aggregate'
      ];
      
      limitKeys.forEach(key => {
        if (configValues[key] !== undefined) {
          limits[key] = configValues[key];
        }
      });
      
      return {
        client_name: configValues.client_name,
        client_address: configValues.client_address,
        use_same_for_cert_holder: configValues.use_same_for_cert_holder,
        certificate_holder_name: configValues.certificate_holder_name,
        certificate_holder_address: configValues.certificate_holder_address,
        additional_insured_name: configValues.additional_insured_name || configValues.client_name,
        additional_insured_operations: configValues.additional_insured_operations,
        limits,
        // Include other fields for different application types
        provider_name: configValues.provider_name,
        practice_name: configValues.practice_name,
        provider_type: configValues.provider_type,
        company_name: configValues.company_name,
        our_party_name: configValues.our_party_name,
        // ... add more as needed
      };
    };
    
    const resetChanges = () => {
      Object.assign(configValues, JSON.parse(JSON.stringify(originalConfig.value)));
      activePreset.value = null;
    };
    
    const getRequirementDisplayValue = (req) => {
      const logic = req.validation_logic || {};
      if (logic.min_amount) {
        return `Min: $${Number(logic.min_amount).toLocaleString()}`;
      }
      if (logic.expected_text) {
        const text = logic.expected_text;
        return text.length > 50 ? text.substring(0, 50) + '...' : text;
      }
      if (logic.criteria) {
        const text = logic.criteria;
        return text.length > 60 ? text.substring(0, 60) + '...' : text;
      }
      return req.description?.substring(0, 50) || '';
    };
    
    const openInLab = (req) => {
      emit('open-lab', req);
    };
    
    const testRequirement = async (req) => {
      if (!hasDocuments.value) {
        alert('No documents available for testing. Please upload a sample document first.');
        return;
      }
      
      testing.value = true;
      testingReqId.value = req.id;
      lastTestedRequirement.value = req;
      testResult.value = null;
      
      try {
        const response = await api.post('/compliance-health/agent/test-prompt/', {
          document_id: documents.value[0].id,
          prompt_template: req.prompt_template || req.validation_logic?.prompt_template,
          requirement_name: req.name,
          severity: req.severity
        });
        
        if (response.data.success) {
          testResult.value = response.data.result;
        }
      } catch (error) {
        console.error('Test error:', error);
        alert('Test failed: ' + (error.response?.data?.error || error.message));
      } finally {
        testing.value = false;
        testingReqId.value = null;
      }
    };
    
    // Watch for check definition changes
    watch(() => props.checkDefinitionId, () => {
      loadConfigSchema();
    });
    
    onMounted(() => {
      loadConfigSchema();
      loadDocuments();
    });
    
    return {
      loading,
      saving,
      testing,
      testingReqId,
      activePreset,
      successMessage,
      testResult,
      lastTestedRequirement,
      updatedRequirementIds,
      showRequirements,
      applicationType,
      schema,
      presets,
      requirements,
      documents,
      configValues,
      expandedSections,
      expandedSubsections,
      hasChanges,
      hasDocuments,
      getFieldComponent,
      shouldHideField,
      toggleSection,
      toggleSubsection,
      applyPreset,
      saveAndApply,
      resetChanges,
      getRequirementDisplayValue,
      openInLab,
      testRequirement
    };
  }
};
</script>

<style scoped>
.client-customizer {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-top: 2rem;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Header */
.customizer-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.header-text h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.header-text p {
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.header-actions .btn {
  border-radius: 8px;
  font-weight: 500;
}

.header-actions .btn-outline-secondary {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
}

.header-actions .btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Loading State */
.loading-state {
  padding: 3rem;
  text-align: center;
  color: #718096;
}

.loading-state p {
  margin-top: 1rem;
}

/* Content */
.customizer-content {
  padding: 1.5rem;
}

/* App Type Badge */
.app-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #f0f4ff;
  color: #667eea;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

/* Presets */
.presets-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
}

.presets-section label {
  display: block;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
}

.preset-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  font-weight: 500;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preset-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.preset-btn.active {
  background: #667eea;
  border-color: #667eea;
  color: white;
}

/* Config Sections */
.config-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  cursor: pointer;
  transition: background 0.2s ease;
}

.section-header:hover {
  background: #f1f5f9;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: #2d3748;
}

.section-title i {
  color: #667eea;
}

.section-content {
  padding: 1.25rem;
}

/* Subsections */
.subsection {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.subsection-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fafbfc;
  cursor: pointer;
  font-weight: 500;
  color: #4a5568;
}

.subsection-header:hover {
  background: #f5f7fa;
}

.coverage-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  min-width: 40px;
  text-align: center;
}

.subsection-fields {
  padding: 1rem;
}

.field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f4f8;
}

.field-row:last-child {
  border-bottom: none;
}

.field-row label {
  font-size: 0.875rem;
  color: #4a5568;
}

.field-row.hidden {
  display: none;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-group.full-width {
  grid-column: span 2;
}

.field-group.hidden {
  display: none;
}

.field-group label {
  font-weight: 500;
  color: #4a5568;
  font-size: 0.875rem;
}

.required {
  color: #e53e3e;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
  outline: none;
}

.number-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-suffix {
  color: #718096;
  font-size: 0.875rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
}

.checkbox-text {
  font-weight: 500;
  color: #4a5568;
}

/* Requirements Preview */
.requirements-preview {
  margin-top: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  cursor: pointer;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: #2d3748;
}

.preview-title i {
  color: #667eea;
}

.count-badge {
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.requirements-list {
  max-height: 400px;
  overflow-y: auto;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f0f4f8;
  transition: background 0.2s ease;
}

.requirement-item:hover {
  background: #f8fafc;
}

.requirement-item:last-child {
  border-bottom: none;
}

.requirement-item.updated {
  background: #f0fff4;
  animation: highlight 0.5s ease;
}

@keyframes highlight {
  0% { background: #c6f6d5; }
  100% { background: #f0fff4; }
}

.req-severity {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: white;
  flex-shrink: 0;
}

.req-severity.critical { background: #e53e3e; }
.req-severity.high { background: #dd6b20; }
.req-severity.medium { background: #d69e2e; }
.req-severity.low { background: #38a169; }

.req-content {
  flex: 1;
  min-width: 0;
}

.req-name {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.9rem;
}

.req-value {
  color: #718096;
  font-size: 0.8rem;
  margin-top: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.req-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Test Result Panel */
.test-result-panel {
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.test-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  font-weight: 500;
  color: #4a5568;
}

.btn-close-sm {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  font-size: 1.25rem;
  color: #718096;
  cursor: pointer;
  line-height: 1;
}

.test-content {
  padding: 1rem;
}

.result-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.result-badge.pass { background: #c6f6d5; color: #276749; }
.result-badge.fail { background: #fed7d7; color: #9b2c2c; }
.result-badge.partial { background: #fefcbf; color: #975a16; }
.result-badge.unclear { background: #e2e8f0; color: #4a5568; }

.result-evidence {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
}

.result-reasoning {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
  color: #718096;
  font-size: 0.85rem;
}

/* Success Message */
.success-message {
  margin-top: 1rem;
  padding: 1rem;
  background: #c6f6d5;
  color: #276749;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .customizer-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .header-content {
    flex-direction: column;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .field-group.full-width {
    grid-column: span 1;
  }
  
  .field-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>

